import { createSelector } from 'reselect';
import { getUserId } from '../auth/selectors';
import { smartTrainingPeriod } from '../../constants/index';
import { fromJS } from 'immutable';

const getApp = state => state.app;

const getInvitationAccepted = state => state.app.get('invitationAccepted');

const getPreselectedPeriod = state => state.app.get('preSelectedPeriod');

const getSmartTrainingPeriod =
        state => state.app.getIn(['smartTraining', 'period']) ||
            fromJS({
                from: smartTrainingPeriod.periodLength - 1,
                to: smartTrainingPeriod.periodLength
            });

const getSmartTrainingFilter =
    state => state.app.getIn(['smartTraining', 'filteredDevice']) || -2;

const getRouteLocation = state => state.router.location;

export const getUserPreferences = state => {
    const userId = getUserId(state);
    if (userId === undefined) return undefined;
const user = `user_${userId}`;
    return state.app.get('savedPreferences')
        ? state.app.get('savedPreferences').get(user) || undefined
        : undefined;
};

export const getSavedMeterId = state => {
    const userPreferences = getUserPreferences(state);
    if (!(userPreferences && userPreferences.size > 0)) return undefined;

    const meterId = userPreferences.get('preselectedMeterId');
    return meterId || meterId === 0 ? meterId : undefined;
};

export const getSavededMeterIds = state => {
    const userPreferences = getUserPreferences(state);
    if (!(userPreferences && userPreferences.size > 0)) return undefined;

    const meterIds = userPreferences.get('selectedMeterIds');    
    
    if (meterIds && meterIds.length > 0) {
        return meterIds.split(',').map(id => parseInt(id, 10));
    }

    return [];
};

const getSavedDeviceId = state => {
    const userPreferences = getUserPreferences(state);
    if (!(userPreferences && userPreferences.size > 0)) return undefined;
    return userPreferences.get('preselectedDeviceId') || 0;
};

const getSavedPeriodId = state => {
    const userPreferences = getUserPreferences(state);
    if (!(userPreferences && userPreferences.size > 0)) return 0;
    return userPreferences.get('preselectedPeriodId') || 0;
};

const getMeters = state => {
    if (state.meters !== undefined && state.meters.get('list')) {
        return state.meters.getIn(['list', 'data']);
    }
    return undefined;
};

const getMeterTypes = state => {
    if (state.meters !== undefined && state.meters.get('types')) {
        return state.meters.getIn(['types', 'data']);
    }
    return undefined;
};

const anyIoTHubSelected = state => {
    const meterIds = getSavededMeterIds(state);
    const meters = getMeters(state);
    const types = getMeterTypes(state);

    if (!meters || !types) {
        return false;
    }

    for (let index = 0; index < meterIds.length; index += 1) {
        const id = meterIds[index];        
        
        const foundMeter = meters.filter(meter => meter.get('id') === id).first();
        const foundType = foundMeter ? foundMeter.get("type") : undefined;

        const meterType = types.filter(type => type.label === foundType)[0];

        if (meterType !== undefined && meterType.isHub === true) {
            return true;
        }
    }

    return false;
}

const getDefaultLegendsVisibility = state => {

    const iotHubSelected = anyIoTHubSelected(state);

    return iotHubSelected ? [false, true, true, false, false, false, false, false, false, true] : [false, false, true, true, true, true, false, false, false, false];
}

export const getSavedGroupById = state => {
    const userPreferences = getUserPreferences(state);
    if (!(userPreferences && userPreferences.size > 0)) return 0;
    return userPreferences.get('preselectedGroupById') || 0;
};

export const getSavedRefresh = state => {
    const userPreferences = getUserPreferences(state);
    if (!(userPreferences && userPreferences.size > 0)) return 0;
    return userPreferences.get('preselectedRefresh') || 5;
};

export const getLegendsVisibility = state => {
    const userPreferences = getUserPreferences(state);
    if (!(userPreferences && userPreferences.size > 0)) return 0;
    const lv = userPreferences.get('legendsVisibility');
    return lv && lv.toJS ? lv.toJS() : getDefaultLegendsVisibility(state);
};

export const getMonitoringVisiblePeriod = createSelector(
    [getPreselectedPeriod],
    period => period
    );

export const selectedLangageSelector = createSelector([getApp], app =>
    app.get('lang', JSON.parse(process.env.APP_CONFIG).APP_LANGUAGE)
);

export const getInvitationAcceptedState = createSelector(
    [getInvitationAccepted],
    invitationAccepted => invitationAccepted
);

export const isFromAdministrationView = createSelector([getRouteLocation], location =>
    location.search.includes('administrationView=true')
);

export const getSavedEnergyFlowPosition = state => {
    const userPreferences = getUserPreferences(state);
    if (!(userPreferences && userPreferences.size > 0)) return undefined;

    const position = userPreferences.get('energyFlowPosition');
    return position !== undefined ? position : 'right';
};

export const getSavedChartMode = state => {
    const userPreferences = getUserPreferences(state);
    if (!(userPreferences && userPreferences.size > 0)) return undefined;

    const chartMode = userPreferences.get('chartMode');
    return chartMode !== undefined ? chartMode : 'phases';
};

export const getPreselectedMeterId = createSelector([getSavedMeterId], id => id);

export const getPreselectedMeterIds = createSelector([getSavededMeterIds], ids => ids);

export const getPreselectedDeviceId = createSelector([getSavedDeviceId], id => id);

export const getPreselectedPeriodId = createSelector([getSavedPeriodId], id => id);

export const getPreselectedGroupById = createSelector([getSavedGroupById], id => id);

export const getPreselectedRefresh = createSelector([getSavedRefresh], id => id);

export const getShowDeletedRecords = createSelector([getApp], app => app.get('showDeletedRecords'));

export const getSmartTrainingMinWh = createSelector([getApp],
    app => app.getIn(['smartTraining', 'minWh']) || 0
);

export const getSmartTrainingPeriodFrom = createSelector([getSmartTrainingPeriod],
    period => period.get('from')
);

export const getSmartTrainingPeriodTo = createSelector([getSmartTrainingPeriod],
    period => period.get('to')
);

export const getSmartTrainingFilteredDevice = createSelector([getSmartTrainingFilter],
    filter => filter
);

export const getPreSelectedEnergyFlowPosition = createSelector([getSavedEnergyFlowPosition], position => position);

export const getPreSelectedChartMode = createSelector([getSavedChartMode], chartMode => chartMode);